/* Header Source Sacrée — comportements du méga menu.
   1. Voile de page à l'ouverture d'un panneau, pour que le menu se détache
      du contenu au lieu de flotter dessus.
   2. Filet sous la barre dès que la page a défilé.
   3. Recherche instantanée de l'atlas des pierres. */

(() => {
  const INITIAL_STONES = 30;

  const normalize = (value) =>
    value
      .toLocaleLowerCase('fr')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

  /* ------------------------------------------------------------------ voile */

  const setupScrim = () => {
    const header = document.querySelector('#header-component');
    if (!header) return;

    const scrim = document.createElement('div');
    scrim.className = 'ss-scrim';
    document.body.appendChild(scrim);

    const root = document.documentElement;
    const isOpen = () => Boolean(header.querySelector('.menu-list__link[aria-expanded="true"]'));

    let frame = 0;
    const sync = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        root.classList.toggle('ss-nav-open', isOpen());
      });
    };

    // Le composant `header-menu` du thème pilote aria-expanded : on suit cet
    // attribut plutôt que les événements de survol, qui se contredisent entre
    // la barre et le panneau.
    new MutationObserver(sync).observe(header, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-expanded'],
    });
  };

  /* ---------------------------------------------------------------- défilement */

  const setupScrollState = () => {
    const root = document.documentElement;
    let ticking = false;

    const update = () => {
      ticking = false;
      root.classList.toggle('ss-scrolled', window.scrollY > 8);
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );

    update();
  };

  /* --------------------------------------------------------------- atlas */

  /* Combien de pierres afficher avant le bouton « tout afficher » ? Assez pour
     remplir le panneau, jamais assez pour le faire déborder : sur un portable
     1280 x 800 il ne reste que ~500 px, et un menu qui se coupe fait amateur.
     On mesure la place réelle plutôt que de figer un nombre. */
  const fitInitialCount = (finder) => {
    const mega = finder.closest('.ss-mega');
    const grid = finder.querySelector('[data-ss-stone-grid]');
    const items = [...finder.querySelectorAll('[data-ss-stone-item]')];
    const sample = items.find((item) => !item.hidden) || items[0];
    if (!mega || !grid || !sample) return INITIAL_STONES;

    const style = getComputedStyle(grid);
    const columns = style.gridTemplateColumns.split(' ').filter(Boolean).length || 6;
    const rowHeight = sample.getBoundingClientRect().height + (parseFloat(style.rowGap) || 0);
    if (!rowHeight) return INITIAL_STONES;

    const headerHeight = document.querySelector('#header-component')?.getBoundingClientRect().height || 72;
    // Tout ce qui n'est pas la grille : en-tête, marges, bouton.
    const chrome = mega.getBoundingClientRect().height - grid.getBoundingClientRect().height;
    const available = window.innerHeight * 0.86 - headerHeight - chrome;
    const rows = Math.max(2, Math.floor(available / rowHeight));

    return Math.min(items.length, rows * columns);
  };

  const updateStoneFinder = (finder) => {
    const input = finder.querySelector('[data-ss-stone-search]');
    const button = finder.querySelector('[data-ss-stone-more]');
    const items = [...finder.querySelectorAll('[data-ss-stone-item]')];
    const query = normalize(input?.value || '');
    const expanded = button?.getAttribute('aria-expanded') === 'true';
    const initialCount = Number(finder.dataset.ssInitialCount) || INITIAL_STONES;
    let visibleCount = 0;

    items.forEach((item, index) => {
      const matches = !query || normalize(item.dataset.stoneTitle || '').includes(query);
      const withinInitialSet = index < initialCount;
      const visible = matches && (query || expanded || withinInitialSet);
      item.hidden = !visible;
      if (visible) visibleCount += 1;
    });

    const empty = finder.querySelector('[data-ss-stone-empty]');
    if (empty) empty.hidden = visibleCount > 0;

    if (button) {
      // Rien à déplier si la place suffisait pour tout afficher.
      button.hidden = Boolean(query) || initialCount >= items.length;
      const label = button.querySelector('[data-ss-stone-more-label]');
      if (label) {
        label.textContent = expanded ? 'Réduire la liste' : `Afficher les ${items.length} pierres`;
      }
    }
  };

  document.addEventListener('input', (event) => {
    if (!event.target.matches('[data-ss-stone-search]')) return;
    const finder = event.target.closest('[data-ss-stone-finder]');
    if (finder) updateStoneFinder(finder);
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-ss-stone-more]');
    if (!button) return;
    const expanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', String(!expanded));
    const finder = button.closest('[data-ss-stone-finder]');
    if (finder) updateStoneFinder(finder);
  });

  /* ------------------------------------------------------------------ boot */

  const setupStoneFinders = () => {
    const finders = [...document.querySelectorAll('[data-ss-stone-finder]')];
    if (!finders.length) return;

    const measure = () => {
      finders.forEach((finder) => {
        finder.dataset.ssInitialCount = String(fitInitialCount(finder));
        updateStoneFinder(finder);
      });
    };

    measure();

    let resizeFrame = 0;
    window.addEventListener(
      'resize',
      () => {
        cancelAnimationFrame(resizeFrame);
        resizeFrame = requestAnimationFrame(measure);
      },
      { passive: true }
    );
  };

  const boot = () => {
    setupScrim();
    setupScrollState();
    setupStoneFinders();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
